import { useState } from 'react'
import { useQuery, useMutation, useQueryClient } from 'react-query'
import { apiService, CreateApiKeyInput } from '../services/api'
import { Plus, Trash2, Key, Lock } from 'lucide-react'

const PROVIDERS = [
  { id: 'mistral', name: 'Mistral AI' },
  { id: 'anthropic', name: 'Anthropic (Claude)' },
  { id: 'openai', name: 'OpenAI' },
  { id: 'assembly', name: 'Assembly.ai' },
  { id: 'elevenlabs', name: 'ElevenLabs' },
  { id: 'other', name: 'Autre...' },
]

const KEY_TYPES = [
  { id: 'API_KEY', name: 'Clé API' },
  { id: 'SECRET', name: 'Secret' },
  { id: 'TOKEN', name: 'Token' },
]

export function ApiKeysManager() {
  const [showAddForm, setShowAddForm] = useState(false)
  const [customProvider, setCustomProvider] = useState('')
  const [newKey, setNewKey] = useState<CreateApiKeyInput>({
    keyType: 'API_KEY',
    provider: 'mistral',
    key: '',
    description: '',
  })

  const queryClient = useQueryClient()

  const { data: apiKeys, isLoading } = useQuery('apiKeys', apiService.getApiKeys)

  const createMutation = useMutation(apiService.createApiKey, {
    onSuccess: () => {
      queryClient.invalidateQueries('apiKeys')
      setShowAddForm(false)
      setNewKey({ keyType: 'API_KEY', provider: 'mistral', key: '', description: '' })
      setCustomProvider('')
    },
  })

  const deleteMutation = useMutation(apiService.deleteApiKey, {
    onSuccess: () => {
      queryClient.invalidateQueries('apiKeys')
    },
  })

  const handleCreate = async () => {
    if (!newKey.key || !newKey.provider || !newKey.keyType) {
      alert('Veuillez remplir tous les champs requis')
      return
    }
    
    // * Use custom provider if "other" is selected
    const providerToUse = newKey.provider === 'other' ? customProvider.trim().toLowerCase() : newKey.provider
    
    if (newKey.provider === 'other' && !customProvider.trim()) {
      alert('Veuillez saisir le nom du fournisseur personnalisé')
      return
    }
    
    await createMutation.mutateAsync({
      ...newKey,
      provider: providerToUse,
    })
  }

  const handleDelete = async (id: string) => {
    if (window.confirm('Êtes-vous sûr de vouloir supprimer cette clé API ? Cette action est irréversible.')) {
      await deleteMutation.mutateAsync(id)
    }
  }

  return (
    <div className="card">
          <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between">
        <div>
          <h2 className="text-lg font-medium text-primary">Clés API</h2>
          <p className="text-sm text-secondary mt-1">
            Gérez vos clés API de manière sécurisée. Les clés sont chiffrées dans la base de données.
            Une fois enregistrées, les clés ne peuvent plus être affichées en clair pour des raisons de sécurité.
            Seule la prévisualisation (8 premiers caractères) est visible pour identification.
          </p>
        </div>
        <button
          onClick={() => setShowAddForm(!showAddForm)}
          className="button button--primary flex items-center gap-2"
        >
          <Plus className="w-4 h-4" />
          <span>Ajouter une clé</span>
        </button>
      </div>

      <div className="px-6 py-6 space-y-4">
        {/* Add form */}
        {showAddForm && (
          <div className="border border-gray-200 rounded-lg p-4 bg-gray-50">
            <h3 className="text-md font-semibold text-primary mb-4">Nouvelle clé API</h3>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-primary mb-1">Fournisseur</label>
                <select
                  value={newKey.provider}
                  onChange={(e) => {
                    setNewKey({ ...newKey, provider: e.target.value })
                    if (e.target.value !== 'other') {
                      setCustomProvider('')
                    }
                  }}
                  className="input"
                >
                  {PROVIDERS.map((provider) => (
                    <option key={provider.id} value={provider.id}>
                      {provider.name}
                    </option>
                  ))}
                </select>
                {newKey.provider === 'other' && (
                  <div className="mt-2">
                    <input
                      type="text"
                      value={customProvider}
                      onChange={(e) => setCustomProvider(e.target.value)}
                      placeholder="Saisir le nom du fournisseur (ex: assembly, groq, etc.)"
                      className="input w-full mt-1"
                    />
                    <p className="text-xs text-gray-500 mt-1">
                      Le nom sera converti en minuscules automatiquement
                    </p>
                  </div>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-primary mb-1">Type de clé</label>
                <select
                  value={newKey.keyType}
                  onChange={(e) => setNewKey({ ...newKey, keyType: e.target.value })}
                  className="input"
                >
                  {KEY_TYPES.map((type) => (
                    <option key={type.id} value={type.id}>
                      {type.name}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-primary mb-1">Clé API</label>
                <input
                  type="password"
                  value={newKey.key}
                  onChange={(e) => setNewKey({ ...newKey, key: e.target.value })}
                  placeholder="sk-..."
                  className="input w-full"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-primary mb-1">
                  Description (optionnel)
                </label>
                <input
                  type="text"
                  value={newKey.description || ''}
                  onChange={(e) => setNewKey({ ...newKey, description: e.target.value })}
                  placeholder="Ex: Clé de production"
                  className="input w-full"
                />
              </div>

              <div className="flex gap-2">
                <button
                  onClick={handleCreate}
                  disabled={createMutation.isLoading}
                  className="button button--primary"
                >
                  {createMutation.isLoading ? 'Création...' : 'Créer'}
                </button>
                <button
                  onClick={() => {
                    setShowAddForm(false)
                    setNewKey({ keyType: 'API_KEY', provider: 'mistral', key: '', description: '' })
                    setCustomProvider('')
                  }}
                  className="button button--secondary"
                >
                  Annuler
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Keys list */}
        {isLoading ? (
          <div className="text-center py-8 text-secondary">Chargement...</div>
        ) : !apiKeys || apiKeys.length === 0 ? (
          <div className="text-center py-8 text-secondary">
            <Key className="w-12 h-12 mx-auto mb-4 text-gray-400" />
            <p>Aucune clé API enregistrée</p>
            <p className="text-sm mt-2">Cliquez sur "Ajouter une clé" pour commencer</p>
          </div>
        ) : (
          <div className="space-y-3">
            {apiKeys.map((key) => (
              <div
                key={key.id}
                className="border border-gray-200 rounded-lg p-4 flex items-center justify-between hover:bg-gray-50"
              >
                <div className="flex-1">
                  <div className="flex items-center gap-3">
                    <Key className="w-5 h-5 text-robot-blue" />
                    <div>
                      <div className="font-medium text-primary">
                        {PROVIDERS.find((p) => p.id === key.provider)?.name || key.provider}
                      </div>
                      <div className="text-sm text-secondary">
                        {key.keyType} • {key.keyPreview}
                      </div>
                      {key.description && (
                        <div className="text-sm text-gray-500 mt-1">{key.description}</div>
                      )}
                    </div>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <div className="flex items-center gap-1 text-xs text-gray-500">
                    <Lock className="w-3 h-3" />
                    <span>Clé chiffrée</span>
                  </div>
                  <button
                    onClick={() => handleDelete(key.id)}
                    disabled={deleteMutation.isLoading}
                    className="p-2 rounded-lg hover:bg-red-100 text-red-600 transition-colors"
                    title="Supprimer"
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  )
}
